import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { LitigationCaseRef, LitigationPosture, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const STATUSES: LitigationPosture[] = ['OPEN', 'SETTLED', 'WON', 'LOST', 'DISCONTINUED'];
const COUNSEL_ROLES = ['LEGISLATIVE_DRAFTER', 'LAW_REVISION_COMMISSIONER'];

/**
 * AG Chambers' watch-list of government litigation — gated to
 * LEGISLATIVE_DRAFTER or LAW_REVISION_COMMISSIONER throughout (see
 * m38-attorney-general/src/routes/litigation.ts). Cross-references M37
 * Judiciary case numbers via a real HTTP call at creation/sync time, never
 * M37's own data directly.
 */
export function Litigation() {
  const { persona } = useAuth();
  const canAccess = persona?.roles.some((r) => COUNSEL_ROLES.includes(r)) ?? false;
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<LitigationPosture | ''>('');
  const [showCreate, setShowCreate] = useState(false);

  const params = new URLSearchParams({ limit: '50' });
  if (q.trim()) params.set('q', q.trim());
  if (status) params.set('status', status);
  const listQ = useApiQuery<Page<LitigationCaseRef>>(canAccess ? `/api/v1/m38/litigation-cases?${params.toString()}` : null);

  return (
    <div>
      <PageHeader
        title="Litigation Watch-list"
        subtitle="Government litigation exposure AG Chambers tracks, cross-referencing M37 Judiciary case numbers."
        actions={
          canAccess ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + New matter
            </button>
          ) : undefined
        }
      />

      {!canAccess ? <RoleNotice roles={COUNSEL_ROLES} /> : null}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Matter / MDA client contains" className="min-w-[220px] flex-1">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} />
        </Field>
        <Field label="Status" className="!mb-0">
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as LitigationPosture | '')}>
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No matters match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Matter</th>
                <th className="table-head">MDA client</th>
                <th className="table-head">M37 case no.</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((m) => (
                <tr key={m.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/ag/litigation/${m.id}`)}>
                  <td className="table-cell font-medium">{m.matterTitle}</td>
                  <td className="table-cell text-xs">{m.mdaClient}</td>
                  <td className="table-cell text-xs">{m.m37CaseNumber ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={m.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateMatterModal
          onClose={() => setShowCreate(false)}
          onDone={(id) => {
            setShowCreate(false);
            navigate(`/ag/litigation/${id}`);
          }}
        />
      ) : null}
    </div>
  );
}

function CreateMatterModal({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const [matterTitle, setMatterTitle] = useState('');
  const [mdaClient, setMdaClient] = useState('');
  const [m37CaseNumber, setM37CaseNumber] = useState('');
  const [assignedCounselNin, setAssignedCounselNin] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<LitigationCaseRef>('/api/v1/m38/litigation-cases', {
        matterTitle,
        mdaClient,
        m37CaseNumber: m37CaseNumber || undefined,
        assignedCounselNin: assignedCounselNin || undefined,
        notes: notes || undefined,
      });
      onDone(created.id);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to open matter');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New litigation matter" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Matter title">
          <input className="field-input" value={matterTitle} onChange={(e) => setMatterTitle(e.target.value)} placeholder="e.g. Republic v. XYZ Holdings — judicial review" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="MDA client">
            <input className="field-input" value={mdaClient} onChange={(e) => setMdaClient(e.target.value)} placeholder="e.g. Ministry of Land Use" />
          </Field>
          <Field label="M37 case number (optional)">
            <input className="field-input" value={m37CaseNumber} onChange={(e) => setM37CaseNumber(e.target.value)} placeholder="e.g. CC-2026-0001234" />
          </Field>
        </div>
        <p className="text-xs text-slate-500">If a case number is given, it is verified against M37 Judiciary over HTTP before this matter is created.</p>
        <Field label="Assigned counsel NIN (optional)">
          <input className="field-input" value={assignedCounselNin} onChange={(e) => setAssignedCounselNin(e.target.value)} />
        </Field>
        <Field label="Notes (optional)">
          <textarea className="field-input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || matterTitle.trim().length < 3 || !mdaClient.trim()}>
            {busy ? 'Creating…' : 'Create matter'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
